import {
  DEFAULT_IMAGE,
  ensureHttps,
  isCollectionItem,
  clampProgress,
  progressColor,
} from '../shared/card'

const LEARNER_NOTE =
  "Each learner sees only what they started, with their own progress. Learners with no courses in progress won't see this block."

// With published content the block shows it as a sample; without any it only has placeholders,
// so the note says where the content will come from instead.
export const adminNote = hasContent =>
  `${hasContent ? 'Sample view.' : 'Your published courses will appear here.'} ${LEARNER_NOTE}`

const INFO_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
  <circle cx="12" cy="12" r="9" stroke="#015FFB" stroke-width="1.6"/>
  <path d="M12 11v5" stroke="#015FFB" stroke-width="1.6" stroke-linecap="round"/>
  <circle cx="12" cy="8" r="1" fill="#015FFB"/>
</svg>`

const ARROW_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
  <path d="M5 12h13m0 0-5-5m5 5-5 5" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const CLOSE_ICON = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
  <path d="m6 6 12 12M18 6 6 18" stroke="#111827" stroke-width="1.8" stroke-linecap="round"/>
</svg>`

const SEARCH_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
  <circle cx="11" cy="11" r="6.5" stroke="#9AA3AF" stroke-width="1.6"/>
  <path d="m16 16 4 4" stroke="#9AA3AF" stroke-width="1.6" stroke-linecap="round"/>
</svg>`

export const renderAdminNote = hasContent => `<div class="continue-learning-note">
  <span class="continue-learning-note-icon">${INFO_ICON}</span>
  <p>${adminNote(hasContent)}</p>
</div>`

const renderRow = item => {
  const value = clampProgress(item.progress)
  // In progress follows the page's primary colour; finished keeps progressColor's green.
  const color =
    value < 100 ? 'var(--page-accent, #015FFB)' : progressColor(value)
  const isCollection = isCollectionItem(item)

  return `
    <div class="your-learning-row" data-catalog-key="${item.key}">
      <div class="your-learning-row-image">
        <img src="${
          !item.thumbnail || item.thumbnail === ''
            ? DEFAULT_IMAGE
            : ensureHttps(item.thumbnail)
        }" alt="${item.name || ''}">
      </div>
      <div class="your-learning-row-body">
        <span class="your-learning-row-type">${
          isCollection ? 'LEARNING PATH' : 'COURSE'
        }</span>
        <p class="your-learning-row-title">${item.name || 'Untitled'}</p>
        <div class="your-learning-row-bar">
          <div style="width: ${value}%; background-color: ${color};"></div>
        </div>
      </div>
      <div class="your-learning-row-value">${value}%</div>
      <button class="your-learning-row-arrow" type="button">${ARROW_ICON}</button>
    </div>
  `
}

/**
 * The tab body is re-rendered on every tab switch and keystroke, so the three empty states
 * live together here: a tab with nothing in it reads differently from a search that missed.
 */
const renderList = (items, searchTerm, tab) => {
  if (!items.length) {
    const message =
      tab === 'completed'
        ? 'No finished content yet'
        : 'Nothing in progress right now'
    return `<div class="your-learning-empty"><p>${message}</p></div>`
  }

  const term = searchTerm.trim().toLowerCase()
  const filtered = term
    ? items.filter(item => (item.name || '').toLowerCase().includes(term))
    : items

  if (!filtered.length) {
    return `<div class="your-learning-empty"><p>Nothing matches that search</p></div>`
  }

  return filtered.map(renderRow).join('')
}

/**
 * CKEditor treats the widget as a single object and swallows keystrokes aimed at anything
 * inside it, which would empty the search box on every letter. The popup is mounted on
 * document.body for that reason, and the input still stops its own events.
 */
const swallowEvents = element => {
  if (!element) return
  ;[
    'keydown',
    'keyup',
    'keypress',
    'input',
    'paste',
    'cut',
    'mousedown',
    'click',
    'focus',
    'blur',
  ].forEach(type => {
    element.addEventListener(type, event => event.stopPropagation())
  })
}

export const openYourLearningPopup = ({
  items = [],
  showAdminNote = true,
  primaryColor,
}) => {
  const existing = document.querySelector('.your-learning-backdrop')
  if (existing) existing.remove()

  const inProgress = items.filter(item => clampProgress(item.progress) < 100)
  const completed = items.filter(item => clampProgress(item.progress) === 100)

  const backdrop = document.createElement('div')
  backdrop.className = 'your-learning-backdrop'
  backdrop.setAttribute('contenteditable', 'false')
  backdrop.setAttribute('data-cke-ignore-events', 'true')
  if (primaryColor) backdrop.style.setProperty('--page-accent', primaryColor)

  backdrop.innerHTML = `
    <div class="your-learning-modal">
      <div class="your-learning-header">
        <h3>Your learning</h3>
        <button class="your-learning-close" type="button">${CLOSE_ICON}</button>
      </div>
      ${showAdminNote ? renderAdminNote(items.length > 0) : ''}
      <div class="your-learning-tabs">
        <button class="your-learning-tab is-active" type="button" data-tab="inProgress">
          In progress <span>${inProgress.length}</span>
        </button>
        <button class="your-learning-tab" type="button" data-tab="completed">
          Completed <span>${completed.length}</span>
        </button>
      </div>
      <div class="your-learning-search">
        ${SEARCH_ICON}
        <input type="text" placeholder="Search your learning"
               data-cke-ignore-events="true">
      </div>
      <div class="your-learning-list"></div>
    </div>
  `

  const modal = backdrop.querySelector('.your-learning-modal')
  const list = backdrop.querySelector('.your-learning-list')
  const searchInput = backdrop.querySelector('.your-learning-search input')
  const tabs = Array.from(backdrop.querySelectorAll('.your-learning-tab'))

  let activeTab = 'inProgress'

  const refresh = () => {
    const source = activeTab === 'completed' ? completed : inProgress
    list.innerHTML = renderList(source, searchInput.value, activeTab)
  }

  tabs.forEach(tab => {
    tab.addEventListener('click', event => {
      event.stopPropagation()
      activeTab = tab.getAttribute('data-tab')
      tabs.forEach(other => other.classList.toggle('is-active', other === tab))
      // Search is scoped to the active tab, so switching tabs starts from a clean box.
      searchInput.value = ''
      refresh()
    })
  })

  swallowEvents(searchInput)
  searchInput.addEventListener('input', refresh)

  const close = () => {
    document.removeEventListener('keydown', onKeyDown, true)
    backdrop.remove()
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      close()
    }
  }

  backdrop.querySelector('.your-learning-close').addEventListener('click', event => {
    event.stopPropagation()
    close()
  })

  backdrop.addEventListener('mousedown', event => {
    event.stopPropagation()
    if (event.target === backdrop) close()
  })

  // Clicks inside the card must not reach the backdrop's close handler.
  modal.addEventListener('click', event => event.stopPropagation())

  document.addEventListener('keydown', onKeyDown, true)
  document.body.appendChild(backdrop)

  refresh()

  return close
}
